import {
  Dropdown,
  Input,
  InputRef,
  Popconfirm,
  Typography,
  MenuProps,
} from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import {
  EditPenIcon,
  DuplicateIcon,
  DeleteIcon,
} from "../../icons-and-animations/Icons";
import "./SceneListSidebar.scss";
import classNames from "classnames";
import confirmationModal from "../BlingsModal/CustomConfirm";
import { validateSceneRename } from "../../helpers/sceneName";

const { TextArea } = Input;

const SceneListItem = ({
  vp,
  index,
  selectedVideoPartName,
  deleteVideoPart,
  renameVideoPart,
  duplicateVideoPart,
  updateVideoPartDescription,
  handleClick,
  allVideoPartsNames,
  selectedIndex,
  hasUnsavedChanges,
  isBlingsAccount,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: vp.name });

  // Parse description and style from the combined field
  const parseDescriptionAndStyle = (combined: string) => {
    if (!combined) return { description: "", style: "" };
    const parts = combined.split("%%__%%");
    return {
      description: parts[0] || "",
      style: parts[1] || "",
    };
  };

  const combineDescriptionAndStyle = (description: string, style: string) => {
    return `${description}%%__%%${style}`;
  };

  const { description: parsedDescription, style: parsedStyle } =
    parseDescriptionAndStyle(vp.description || "");

  // State definitions
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [newName, setNewName] = useState(vp.name);
  const [newDescription, setNewDescription] = useState(parsedDescription);
  const [newStyle, setNewStyle] = useState(parsedStyle);
  const [originalName, setOriginalName] = useState(vp.name);
  const [originalDescription, setOriginalDescription] =
    useState(parsedDescription);
  const [originalStyle, setOriginalStyle] = useState(parsedStyle);
  const [error, setError] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [showPopConfirm, setShowPopConfirm] = useState(false);
  const [canDrag, setCanDrag] = useState(true);
  const [isTruncated, setIsTruncated] = useState(false);
  const inputRef = useRef<InputRef | any>(null);
  const descriptionInputRef = useRef<InputRef | any>(null);
  const styleInputRef = useRef<InputRef | any>(null);
  const sceneNameRef = useRef<HTMLParagraphElement>(null);

  // Function definitions
  const handleMenuClick = ({ key }) => {
    switch (key) {
      case "rename":
        setIsEditing(true);
        setOriginalName(newName);
        setError("");
        break;
      case "editDescription":
        setIsEditingDescription(true);
        setOriginalDescription(newDescription);
        setOriginalStyle(newStyle);
        break;
      case "duplicate":
        duplicateVideoPart(vp.name);
        break;
      case "delete":
        setShowPopConfirm(true);
        break;
      default:
        break;
    }
  };

  const handleConfirmDelete = () => {
    deleteVideoPart(vp.name);
    setShowPopConfirm(false);
  };

  const handleCancelDelete = () => {
    setShowPopConfirm(false);
  };

  const handleRenameBlur = () => {
    const result = validateSceneRename(newName, vp.name, allVideoPartsNames);
    if (result.error) {
      setError(result.error);
      return;
    }
    setIsEditing(false);
    setNewName(result.name);
    if (result.name !== vp.name) renameVideoPart(vp.name, result.name);
  };

  const handleDescriptionBlur = () => {
    setIsEditingDescription(false);
    const combinedValue = combineDescriptionAndStyle(newDescription, newStyle);
    if (combinedValue !== vp.description) {
      updateVideoPartDescription(vp.name, combinedValue);
    }
  };

  const handleRenameChange = (e) => {
    setNewName(e.target.value);
    setError(
      validateSceneRename(e.target.value, vp.name, allVideoPartsNames).error ||
        ""
    );
  };

  const handleDescriptionChange = (e) => {
    setNewDescription(e.target.value);
  };

  const handleStyleChange = (e) => {
    setNewStyle(e.target.value);
  };

  const handleRenameKeyPress = (e) => {
    if (e.key === "Enter") {
      handleRenameBlur();
    } else if (e.key === "Escape") {
      setNewName(originalName);
      setIsEditing(false);
      setError("");
    }
  };

  const handleDescriptionKeyPress = (e) => {
    if (e.key === "Enter" && e.shiftKey) {
      return;
    } else if (e.key === "Enter") {
      handleDescriptionBlur();
    } else if (e.key === "Escape") {
      setNewDescription(originalDescription);
      setNewStyle(originalStyle);
      setIsEditingDescription(false);
    }
  };

  const handleStyleKeyPress = (e) => {
    if (e.key === "Enter" && e.shiftKey) {
      return;
    } else if (e.key === "Enter") {
      handleDescriptionBlur();
    } else if (e.key === "Escape") {
      setNewDescription(originalDescription);
      setNewStyle(originalStyle);
      setIsEditingDescription(false);
    }
  };

  // Effect definitions
  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  useEffect(() => {
    if (isEditingDescription) {
      descriptionInputRef.current?.focus();
    }
  }, [isEditingDescription]);

  useEffect(() => {
    const { description, style } = parseDescriptionAndStyle(
      vp.description || ""
    );
    setNewDescription(description);
    setNewStyle(style);
  }, [vp.description]);

  useEffect(() => {
    if (isEditing || isEditingDescription) {
      setCanDrag(false);
    } else {
      setCanDrag(true);
    }
  }, [isEditing, isEditingDescription]);

  // Calculate scroll distance for text carousel and check if truncated
  useEffect(() => {
    const element = sceneNameRef.current;
    if (element) {
      const containerWidth = element.offsetWidth;
      const textWidth = element.scrollWidth;
      const scrollDistance = textWidth - containerWidth;

      if (scrollDistance > 0) {
        element.style.setProperty("--scroll-distance", `-${scrollDistance}px`);
        setIsTruncated(true);
      } else {
        element.style.setProperty("--scroll-distance", "0px");
        setIsTruncated(false);
      }
    }
  }, [vp.name]);

  const style =
    transform && canDrag
      ? {
          transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
          transition: isDragging ? "unset" : transition,
          opacity: isDragging ? 0.5 : 1,
        }
      : {};

  const items: MenuProps["items"] = [
    {
      label: "Rename scene",
      key: "rename",
      icon: (
        <div className="icon-margin">
          <EditPenIcon />
        </div>
      ),
    },
    ...(isBlingsAccount
      ? [
          {
            label: "Edit description and style",
            key: "editDescription",
            icon: (
              <div className="icon-margin">
                <EditPenIcon />
              </div>
            ),
          },
        ]
      : []),
    {
      label: "Duplicate scene",
      key: "duplicate",
      icon: (
        <div className="icon-margin">
          <DuplicateIcon />
        </div>
      ),
    },
    {
      label: "Delete scene",
      key: "delete",
      icon: (
        <div className="icon-margin">
          <DeleteIcon />
        </div>
      ),
    },
  ];

  const handleUnsavedChanges = async (action: () => void) => {
    if (hasUnsavedChanges) {
      await confirmationModal.warning(
        "You have pending changes. Please save or discard them before proceeding."
      );
      return;
    } else {
      action();
    }
  };

  const menuProps: MenuProps = {
    items,
    onClick: (key) => handleUnsavedChanges(() => handleMenuClick(key)),
  };

  const isSelected = selectedVideoPartName === vp.name;

  return (
    <div
      className={classNames("scene-list-item", {
        selected: isSelected,
        editing: isEditing || isEditingDescription,
        dragging: isDragging,
      })}
      key={vp.name}
      ref={setNodeRef}
      style={style}
      data-scene-name={vp.name}
      {...(canDrag ? attributes : {})}
      {...(canDrag ? listeners : {})}
    >
      <div
        className="scene-item-content"
        onClick={() => {
          if (
            !menuOpen &&
            !showPopConfirm &&
            !isEditing &&
            !isEditingDescription
          ) {
            handleClick();
          }
        }}
      >
        {isEditing ? (
          <Popconfirm
            icon={false}
            onCancel={() => {
              setNewName(originalName);
              setIsEditing(false);
              setError("");
            }}
            onConfirm={handleRenameBlur}
            title={
              <div style={{ maxWidth: "250px" }}>
                <Input
                  ref={inputRef}
                  value={newName}
                  onChange={handleRenameChange}
                  onBlur={handleRenameBlur}
                  onKeyDown={handleRenameKeyPress}
                  size="small"
                  status={error ? "error" : ""}
                />
                {error && (
                  <Typography.Text type="danger" style={{ color: "#d9d9d9" }}>
                    {error}
                  </Typography.Text>
                )}
              </div>
            }
            open={true}
          />
        ) : isEditingDescription ? (
          <Popconfirm
            icon={false}
            onCancel={() => {
              setNewDescription(originalDescription);
              setNewStyle(originalStyle);
              setIsEditingDescription(false);
            }}
            onConfirm={handleDescriptionBlur}
            title={
              <div style={{ maxWidth: "300px" }}>
                <div style={{ marginBottom: "8px" }}>
                  <Typography.Text strong>Description:</Typography.Text>
                  <TextArea
                    ref={descriptionInputRef}
                    value={newDescription}
                    onChange={handleDescriptionChange}
                    onKeyDown={handleDescriptionKeyPress}
                    rows={3}
                    style={{
                      marginTop: "4px",
                      backgroundColor: "var(--ant-input-active-bg)",
                    }}
                    placeholder="Enter description..."
                  />
                </div>
                <div>
                  <Typography.Text strong>Style:</Typography.Text>
                  <TextArea
                    ref={styleInputRef}
                    value={newStyle}
                    onChange={handleStyleChange}
                    onKeyDown={handleStyleKeyPress}
                    rows={3}
                    style={{
                      marginTop: "4px",
                      backgroundColor: "var(--ant-input-active-bg)",
                    }}
                    placeholder="Enter style..."
                  />
                </div>
              </div>
            }
            open={true}
          />
        ) : null}

        <div className="scene-name-wrapper">
          <p
            className={classNames("scene-name", { truncated: isTruncated })}
            title={vp.name}
            ref={sceneNameRef}
          >
            {vp.name}
          </p>
        </div>

        <Dropdown
          menu={menuProps}
          overlayClassName="dropdown-menu"
          trigger={["click"]}
          onOpenChange={(visible) => setMenuOpen(visible)}
        >
          <button
            type="button"
            className="scene-menu-button"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreOutlined />
          </button>
        </Dropdown>
      </div>

      <Popconfirm
        title="Are you sure to delete this part?"
        open={showPopConfirm}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        okText="Yes"
        cancelText="No"
      />
    </div>
  );
};

export default SceneListItem;
